import { useEffect, useMemo } from 'react';
import {
  STORAGE_KEY,
  TECHLAND_DEFAULT_AGENT_ID,
  THINKING_MODES,
} from '../../config/constant.ts';
import { useRequestController } from '../../Home/hooks/useRequestController.ts';
import { useLocalState } from '../../ChatPage/hooks/useLocalState';
import { shallowEqual, useSelector } from 'umi';
import { get, includes, sortBy, some } from 'lodash-es';
import { parseJsonSafely } from '../../ChatPage/utils/index.ts';
import { HIDDEN_ORGANIZATIONS } from '@/pages/aigc/components/OrganizationPermission';
import { IAgentData } from '../types/index.js';
import { TEST_COLLECTION_DATA } from '../../config/testRainbow.ts';
import {
  hasAgentPermission,
  isAgentAccessible,
} from '../utils';

/**
 * 获取 Agent 模型和分类数据的 Hook
 * 根据当前选中的 Agent ID 从后端获取对应的模型列表和分类列表
 *
 * 单一数据源策略（避免 UI 与 localStorage 分裂）：
 * - selectedAgentId 由 useLocalState（内存 store，跨实例通过 subscribe 实时同步）承载，作为 UI 唯一数据源；
 * - localStorage 仅作为持久化镜像（供 getAgentConfig 在提交时读取），初始值用作 useLocalState 的种子；
 * - 所有写入统一走 writeAgentId：同时写 localStorage 与内存 store，两层永远一致。
 *
 * 权限：rainbow / mgmt / dashboard（userMessage）并发；按 agent.permissionSource 分流（默认 rainbow）。
 */
export const useAgentModelsData = () => {
  // 获取用户信息用于权限判断
  const userInfo = useSelector(
    (state: any) => state?.home?.userInfo,
    shallowEqual,
  );

  // 获取用户的组织列表
  const userOrganizations: string[] = userInfo?.organization || [];

  // 判断用户是否属于需要隐藏的组织
  const shouldHide = some(userOrganizations, (org: string) =>
    includes(HIDDEN_ORGANIZATIONS, org),
  );

  // 请求 Agent 模型和分类数据（七彩石）
  const configData = useRequestController({
    method: 'POST',
    url: `/api/v1/permission/rainbow`,
    refreshDeps: useMemo(() => ({ key: '', group: 'agent.collection' }), []),
    // 默认2个小时不发起请求
    staleTime: 7200000,
    cacheKey: 'agent-models-data',
    onError: () => {
      configData?.mutate(TEST_COLLECTION_DATA);
    },
    leaveRequest: true,
  });

  // 并发：mgmt 全量权限（Agent 有权条件：permission_key=agent 且 readable=1）
  const mgmtPermissionData = useRequestController({
    method: 'POST',
    url: `/api/v1/mgmt_pc/permission/user_permission`,
    refreshDeps: useMemo(() => ({}), []),
    staleTime: 7200000,
    cacheKey: 'agent-mgmt-user-permission',
    leaveRequest: true,
  });

  // 并发：经分游戏列表（与 ChartPopover 共用 cacheKey，避免重复请求）
  const dashboardUserMessage = useRequestController({
    method: 'POST',
    url: '/api/v1/dashboard/userMessage',
    refreshDeps: useMemo(() => ({ style_version: 'v2' }), []),
    cacheTime: 1800000,
    staleTime: 1800000,
    cacheKey: 'new-chart-game-list',
    leaveRequest: true,
  });

  // 三条权限请求都结束前不要产出授权列表 / 写默认 agent，避免半成品被下游当成就绪
  const isPermissionSettled =
    !configData.loading &&
    !mgmtPermissionData.loading &&
    !dashboardUserMessage.loading;

  const userOpenId = userInfo?.openid;
  const storageKey = `${STORAGE_KEY}_${userOpenId}__`;

  /** mgmt permissions；http 已解包 data，直接取 permissions */
  const mgmtPermissions = useMemo(
    () => get(mgmtPermissionData, 'data.permissions', []),
    [mgmtPermissionData?.data],
  );

  /** 经分 main_game（含子版本 game_List） */
  const dashboardGames = useMemo(
    () => get(dashboardUserMessage, 'data.main_game', []),
    [dashboardUserMessage?.data],
  );

  // 读取 localStorage 中已持久化的 agentId（getAgentConfig 提交时读取的同一真源）
  const readStoredAgentId = (): string => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (parseJsonSafely(raw) as string) || '' : '';
    } catch {
      return '';
    }
  };

  // 唯一响应式数据源：内存 store 跨实例同步；以 localStorage 值作为初始种子
  const [selectedAgentId, setSelectedAgentId] = useLocalState<string>(
    storageKey,
    readStoredAgentId(),
  );

  // 唯一写入路径：同时写 localStorage（持久化 + 提交读取）与内存 store（广播给所有实例，驱动 UI），两层永远一致
  const writeAgentId = (agentId: string): void => {
    if (!agentId) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(agentId));
    } catch {
      // ignore
    }
    setSelectedAgentId(agentId);
  };

  const realData = useMemo(() => {
    const dataSource = configData?.data?.rows?.map((row) => {
      const parsedValue = parseJsonSafely(row.value);
      return {
        id: row.key,
        ...row,
        ...parsedValue,
      };
    });
    return sortBy(dataSource, ['sort']);
  }, [configData?.data?.rows]);

  useEffect(() => {
    const currentStored = readStoredAgentId();

    // localStorage 非空即为已持久化的选择：一律尊重、绝不覆盖，仅把内存 store 对齐到它，
    // 保证 UI 与提交真源一致（避免加载竞态 / 校验假阴性把已选值重置成默认值）。
    if (currentStored) {
      if (selectedAgentId !== currentStored) {
        setSelectedAgentId(currentStored);
      }
      return;
    }

    // localStorage 确实为空：等权限请求与 realData 都到位后再写默认值。
    if (!isPermissionSettled || !realData?.length) {
      return;
    }

    const permissionContext = { userOpenId, mgmtPermissions, dashboardGames };
    let initialSelected: string | undefined;

    // 如果是 Techland 组织，优先查找 TECHLAND_DEFAULT_AGENT_ID
    if (shouldHide) {
      initialSelected = realData?.find(
        (agent) =>
          agent?.agentId === TECHLAND_DEFAULT_AGENT_ID &&
          isAgentAccessible(agent, permissionContext),
      )?.agentId;
    }

    // 如果没有找到 TECHLAND_DEFAULT_AGENT_ID 或不是 Techland 组织，查找默认选中项
    if (!initialSelected) {
      initialSelected = realData?.find(
        (agent) =>
          agent?.isDefaultSelected &&
          isAgentAccessible(agent, permissionContext),
      )?.agentId;
    }

    const firstAccessible = realData.find((agent) =>
      isAgentAccessible(agent, permissionContext),
    )?.agentId;
    writeAgentId(initialSelected || firstAccessible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAgentId,
    shouldHide,
    realData,
    userOpenId,
    mgmtPermissions,
    dashboardGames,
    isPermissionSettled,
  ]);

  // 使用单个 useMemo 提取模型列表和分类列表
  const { modelList, categoryList } = useMemo(() => {
    if (!realData) {
      return { modelList: THINKING_MODES, categoryList: [] };
    }
    // 根据 selectedAgentId 查找对应的 agent 数据
    const currentAgent = realData?.find(
      (agent: any) => agent.agentId === selectedAgentId,
    );
    const modeList = THINKING_MODES?.filter((item: any) =>
      currentAgent?.thinkingMode?.includes(item.agentId),
    );
    return {
      modelList: modeList || realData?.[0]?.thinkingMode || [],
      categoryList:
        currentAgent?.recommendQuestions ||
        realData?.[0]?.recommendQuestions ||
        [],
    };
  }, [realData, selectedAgentId]);

  // 过滤出已发布的数据，并按 permissionSource 添加权限判断
  const { allAgents, authorizedAgents } = useMemo(() => {
    const result = {
      allAgents: [] as IAgentData[],
      authorizedAgents: [] as IAgentData[],
    };

    realData?.forEach((agent) => {
      if (agent.status !== 'Published') return;

      const hasPermission = hasAgentPermission({
        agent,
        userOpenId,
        mgmtPermissions,
        dashboardGames,
      });

      const processed = {
        ...agent,
        hasPermission,
      };

      result.allAgents.push(processed);

      if (hasPermission) {
        result.authorizedAgents.push({
          ...processed,
          description_cn: agent?.shortDescription_cn || agent?.description_cn || '',
          description: agent?.shortDescription || agent?.description || '',
        });
      }
    });

    return {
      allAgents: result.allAgents,
      authorizedAgents: isPermissionSettled ? result.authorizedAgents : [],
    };
  }, [realData, userOpenId, mgmtPermissions, dashboardGames, isPermissionSettled]);

  // 根据 selectedAgentId 查找对应的 agent 数据（从全部数据中查找）
  const currentData = allAgents?.find(
    (agent) => agent?.agentId === selectedAgentId,
  );

  // 切换 Agent：统一走唯一写入路径，同时写 localStorage 与内存 store，两层永远一致
  const handleAgentClick = (agentId: string): void => {
    writeAgentId(agentId);
  };

  return {
    ...configData,
    allAgents,
    authorizedAgents,
    currentData,
    modelList,
    categoryList,
    selectedAgentId,
    handleAgentClick,
  };
};
